/* eslint-disable unicorn/prefer-module */
/* eslint-disable no-console */
import { useState, useEffect, useMemo, useCallback, useRef } from "react";
import { Dropdown } from "react-bootstrap";
import { FaPlay, FaPause, FaTrash, FaUpload } from "react-icons/fa";
import { FaBackwardStep, FaForwardStep } from "react-icons/fa6";
import { IoIosCloudDownload } from "react-icons/io";
import Slider from "react-slick";
import "slick-carousel/slick/slick.css";
import "slick-carousel/slick/slick-theme.css";
import { GALERIA_ADMIN_IDS } from "../../constants/permisos";
import { useAuthContext } from "../../context/auth";
import { getEdificiosFullPublico, getClientes, getCategorias, getFotosGaleria, deleteFotoGaleria } from "../../utils";
import { estamparLogoEnImagen } from "../../utils/logoOperacion";
import SubirFotoModal from "./SubirFotoModal";
import "./stylesGaleria.scss";

// Función para barajar un array (Fisher-Yates)
const shuffleArray = (array) => {
  const shuffled = [...array];
  for (let i = shuffled.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]];
  }
  return shuffled;
};

// Función para capitalizar categorías
const capitalizeFirst = (str) => {
  if (!str) return str;
  const parts = str.split(" & ");
  return parts
    .map((part) =>
      part
        .trim()
        .split(" ")
        .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
        .join(" ")
    )
    .join(" & ");
};

const Galeria = () => {
  const [photos, setPhotos] = useState([]);
  const [filteredPhotos, setFilteredPhotos] = useState([]);
  const [filters, setFilters] = useState({
    category: "",
    client: "",
    building: "",
    country: "",
    screenType: "",
  });
  const [edificios, setEdificios] = useState({});
  const [categorias, setCategorias] = useState({});
  const [clientes, setClientes] = useState({});
  const { pais, id: userId, session } = useAuthContext();
  const isAdmin = GALERIA_ADMIN_IDS.includes(userId);
  const tieneSesion = Boolean(session);
  const [isPlaying, setIsPlaying] = useState(true);
  const sliderRef = useRef(null);
  const [currentSlide, setCurrentSlide] = useState(0);
  const [showUploadModal, setShowUploadModal] = useState(false);

  const mapFotosFromDB = (fotosData) =>
    fotosData.map((foto) => ({
      id: foto.id,
      src: foto.url,
      idcliente: foto.idcliente,
      idedificio: foto.idedificio,
      screenType: foto.screen_type,
      alt: `foto-${foto.id}`,
      _dbRecord: foto,
    }));

  useEffect(() => {
    const loadData = async () => {
      try {
        const [edData, catData, fotosData] = await Promise.all([
          getEdificiosFullPublico(),
          getCategorias(),
          getFotosGaleria(),
        ]);
        if (edData?.length > 0) {
          setEdificios(
            edData.reduce((acc, e) => {
              acc[e.id] = { edificio: e.edificio || "Sin Nombre", pais: e.pais || "Sin País" };
              return acc;
            }, {})
          );
        }
        if (catData?.length > 0) {
          setCategorias(catData.reduce((acc, c) => ({ ...acc, [c.id]: c.nombre }), {}));
        }
        const mapped = mapFotosFromDB(fotosData);
        const shuffled = shuffleArray(mapped);
        setPhotos(shuffled);
        setFilteredPhotos(shuffled);
      } catch (error) {
        console.error("Error cargando datos de galería:", error);
      }
    };
    loadData();
  }, []);

  // Clientes: para CUALQUIER usuario con sesión, de todos los países — la galería existe para
  // mostrar la experiencia de Chile y de Perú a todos, sin importar el país de la sesión
  // (decisión de Ignacio, 2026-10-05; revierte el filtro por país de sesión y el gate a
  // GALERIA_ADMIN_IDS introducidos en 37acdbad, que eran un error de criterio, no un fix de bug).
  // Un visitante SIN sesión sigue sin disparar esta query (10.279, el gate corre antes del
  // fetch) — eso sí era el problema de seguridad real (plan RLS tanda A3). GALERIA_ADMIN_IDS
  // sigue gobernando solo subir/borrar fotos (botones e isAdmin de abajo), no esta lectura.
  useEffect(() => {
    if (!tieneSesion) return;
    const loadClientes = async () => {
      try {
        const cliData = await getClientes({});
        if (cliData?.length > 0) {
          setClientes(cliData.reduce((acc, c) => ({ ...acc, [c.id]: c }), {}));
        }
      } catch (error) {
        console.error("Error cargando clientes de galería:", error);
      }
    };
    loadClientes();
  }, [tieneSesion]);

  // Mapa para convertir screenType legible a código
  const screenTypeMap = {
    Ascensores: "asc",
    Circulación: "cir",
    Espera: "esp",
    Cowork: "cow",
    "Directorios Digitales": "dir",
  };

  // Helper para aplicar filtros
  const applyFiltersHelper = useCallback(
    (currentFilters, photosList) => {
      let filtered = [...photosList];
      if (currentFilters.category) {
        filtered = filtered.filter((photo) => {
          if (photo.idcliente && clientes[photo.idcliente]?.categoria) {
            const categoriaId = clientes[photo.idcliente].categoria;
            const categoriaNombre = capitalizeFirst(categorias[categoriaId]);
            return categoriaNombre === currentFilters.category;
          }
          return "Sin Categoría" === currentFilters.category;
        });
      }
      if (currentFilters.client) {
        filtered = filtered.filter((photo) => {
          if (!photo.idcliente || !clientes[photo.idcliente]?.nombre) return false;
          const c = clientes[photo.idcliente];
          return `${c.nombre}, ${c.pais || "Sin País"}` === currentFilters.client;
        });
      }
      if (currentFilters.building) {
        filtered = filtered.filter((photo) => {
          const buildingName = edificios[photo.idedificio]?.edificio || "Sin Edificio";
          return buildingName === currentFilters.building;
        });
      }
      if (currentFilters.country) {
        filtered = filtered.filter((photo) => {
          const clientePais = photo.idcliente ? clientes[photo.idcliente]?.pais : null;
          const edificioPais = edificios[photo.idedificio]?.pais || null;
          return (clientePais || edificioPais || "Sin País") === currentFilters.country;
        });
      }
      if (currentFilters.screenType) {
        filtered = filtered.filter((photo) => photo.screenType === screenTypeMap[currentFilters.screenType]);
      }
      return filtered;
    },
    [clientes, categorias, edificios]
  );

  // Opciones dinámicas para dropdowns
  const categoryOptions = useMemo(() => {
    const tempFilters = { ...filters, category: "" };
    const filtered = applyFiltersHelper(tempFilters, photos);
    const cats = new Set(
      filtered
        .map((p) => {
          if (p.idcliente && clientes[p.idcliente]?.categoria) {
            const categoriaId = clientes[p.idcliente].categoria;
            const rawCategory = categorias[categoriaId];
            return capitalizeFirst(rawCategory);
          }
          return null;
        })
        .filter(Boolean)
    );
    return [...cats].sort();
  }, [filters, clientes, categorias, photos, applyFiltersHelper]);

  const clientOptions = useMemo(() => {
    const tempFilters = { ...filters, client: "" };
    const filtered = applyFiltersHelper(tempFilters, photos);
    const clientsSet = new Set(
      filtered
        .map((p) => {
          if (!p.idcliente || !clientes[p.idcliente]?.nombre) return null;
          const c = clientes[p.idcliente];
          return `${c.nombre}, ${c.pais || "Sin País"}`;
        })
        .filter(Boolean)
    );
    return [...clientsSet].sort();
  }, [filters, clientes, photos, applyFiltersHelper]);

  const buildingOptions = useMemo(() => {
    const tempFilters = { ...filters, building: "" };
    const filtered = applyFiltersHelper(tempFilters, photos);
    const buildingsSet = new Set(filtered.map((p) => edificios[p.idedificio]?.edificio).filter(Boolean));
    return [...buildingsSet].sort();
  }, [filters, edificios, photos, applyFiltersHelper]);

  const countryOptions = useMemo(() => {
    const tempFilters = { ...filters, country: "" };
    const filtered = applyFiltersHelper(tempFilters, photos);
    const countriesSet = new Set(
      filtered
        .map((p) => {
          const clientePais = p.idcliente ? clientes[p.idcliente]?.pais : null;
          const edificioPais = edificios[p.idedificio]?.pais || null;
          return clientePais || edificioPais || null;
        })
        .filter(Boolean)
    );
    return [...countriesSet].sort();
  }, [filters, clientes, edificios, photos, applyFiltersHelper]);

  const screenTypeOptions = useMemo(() => {
    const tempFilters = { ...filters, screenType: "" };
    const filtered = applyFiltersHelper(tempFilters, photos);
    const typesSet = new Set(
      filtered.map((p) => Object.entries(screenTypeMap).find(([key, val]) => val === p.screenType)?.[0]).filter(Boolean)
    );
    return [...typesSet].sort();
  }, [filters, photos, applyFiltersHelper]);

  // Aplicar filtros
  const applyFilters = (currentFilters) => {
    const filtered = applyFiltersHelper(currentFilters, photos);
    setFilteredPhotos(filtered);
  };

  // Manejar cambio en filtros
  const handleFilterChange = (name, value) => {
    const newFilters = { ...filters, [name]: value };
    setFilters(newFilters);
    applyFilters(newFilters);
  };
  const sliderSettings = {
    dots: false,
    infinite: true,
    speed: 500,
    slidesToShow: 1,
    slidesToScroll: 1,
    autoplay: true,
    autoplaySpeed: 4500,
    pauseOnHover: false,
    arrows: false,
    responsive: [
      { breakpoint: 1024, settings: { slidesToShow: 1 } },
      { breakpoint: 768, settings: { slidesToShow: 1 } },
    ],
  };

  const logo = useMemo(
    () =>
      ({
        Chile: require("../../img/wecast-logo.png"),
        Perú: require("../../img/visiona-logo.png"),
        Region: require("../../img/adigital-logo.png"),
      })[pais] || require("../../img/visiona-logo.png"),
    [pais]
  );

  // Función para retroceder imagen
  const goToPrev = () => {
    if (sliderRef.current) {
      sliderRef.current.slickPrev();
    }
  };

  // Función para avanzar imagen
  const goToNext = () => {
    if (sliderRef.current) {
      sliderRef.current.slickNext();
    }
  };

  // Función para alternar pausa/play
  const togglePlayPause = () => {
    if (sliderRef.current) {
      if (isPlaying) {
        sliderRef.current.slickPause();
      } else {
        sliderRef.current.slickPlay();
      }
      setIsPlaying(!isPlaying);
    }
  };

  // Recargar fotos después de subir o eliminar
  const refreshPhotos = async () => {
    try {
      const fotosData = await getFotosGaleria();
      const mapped = mapFotosFromDB(fotosData);
      const shuffled = shuffleArray(mapped);
      setPhotos(shuffled);
      setFilteredPhotos(shuffled);
      setCurrentSlide(0);
      if (sliderRef.current) sliderRef.current.slickGoTo(0);
    } catch (error) {
      console.error("Error recargando galería:", error);
    }
  };

  // Eliminar foto actual
  const handleDeletePhoto = async () => {
    const photo = filteredPhotos[currentSlide];
    if (!photo?._dbRecord) return;
    if (!window.confirm("¿Eliminar esta foto de la galería?")) return;
    try {
      await deleteFotoGaleria(photo._dbRecord);
      await refreshPhotos();
    } catch (error) {
      console.error("Error eliminando foto:", error);
    }
  };

  // Función para descargar la imagen actual con el logo superpuesto — estampado por la función
  // compartida `estamparLogoEnImagen` (10.1; también la usa el adjunto de fotos del Radar
  // Comercial, ver `src/utils/logoOperacion.js`).
  const downloadImage = async () => {
    const activeSlideIndex = sliderRef.current?.innerSlider?.state.currentSlide;
    if (filteredPhotos[activeSlideIndex]) {
      const photo = filteredPhotos[activeSlideIndex];
      const dataUrl = await estamparLogoEnImagen({ url: photo.src, pais });
      const countryPrefix = pais === "Chile" ? "Wecast" : pais === "Perú" ? "Visiona" : "Adigital";
      const clientId = photo.idcliente;
      const category =
        clientId && clientes[clientId]?.categoria ? capitalizeFirst(categorias[clientes[clientId].categoria]) : "";
      const client = clientId && clientes[clientId]?.nombre ? clientes[clientId].empresa : "";
      const building = photo.idedificio && edificios[photo.idedificio] ? edificios[photo.idedificio].edificio : "";
      const fileNameParts = [countryPrefix];
      if (category) fileNameParts.push(category);
      if (client) fileNameParts.push(client);
      if (building) fileNameParts.push(building);
      const fileName = `${fileNameParts.join(". ")}.jpg`;
      const link = document.createElement("a");
      link.href = dataUrl;
      link.download = fileName;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    }
  };

  // Obtener datos de la foto actual
  const getCurrentPhotoData = () => {
    if (filteredPhotos[currentSlide]) {
      const photo = filteredPhotos[currentSlide];
      const clientId = photo.idcliente;
      const buildingData = photo.idedificio ? edificios[photo.idedificio] : null;
      const building = buildingData ? buildingData.edificio : "Sin Edificio";
      const screenType =
        Object.entries(screenTypeMap).find(([, val]) => val === photo.screenType)?.[0] || "Sin Circuito";

      let client, category;
      let country = buildingData?.pais || "Sin País";

      if (clientId && clientes[clientId]) {
        client = clientes[clientId].nombre || "Sin Cliente";
        category = clientes[clientId].categoria
          ? capitalizeFirst(categorias[clientes[clientId].categoria])
          : "Sin Categoría";
        if (clientes[clientId].pais) country = clientes[clientId].pais;
      }

      return { client, category, screenType, building, country };
    }
    return {
      client: undefined,
      category: undefined,
      screenType: "Sin Circuito",
      building: "Sin Edificio",
      country: "Sin País",
    };
  };

  return (
    <div className="gallery-container">
      <div className="left-section">
        <div className="filters-group">
          <Dropdown onSelect={(value) => handleFilterChange("category", value)}>
            <Dropdown.Toggle
              id="dropdown-category"
              className={`text-start ${filters.category !== "" ? "selected" : ""}`}
              data-selected={filters.category !== "" ? "true" : undefined}
            >
              {filters.category ? capitalizeFirst(filters.category) : "Categorías"}
            </Dropdown.Toggle>
            <Dropdown.Menu className="custom-dropdown-menu">
              <Dropdown.Item eventKey="">{filters.category ? "Limpiar" : "Seleccione"}</Dropdown.Item>
              {categoryOptions.map((catName) => (
                <Dropdown.Item key={catName} eventKey={catName}>
                  {catName}
                </Dropdown.Item>
              ))}
            </Dropdown.Menu>
          </Dropdown>
          <Dropdown onSelect={(value) => handleFilterChange("client", value)}>
            <Dropdown.Toggle
              id="dropdown-client"
              className={`text-start ${filters.client !== "" ? "selected" : ""}`}
              data-selected={filters.client !== "" ? "true" : undefined}
            >
              {filters.client || "Clientes"}
            </Dropdown.Toggle>
            <Dropdown.Menu className="custom-dropdown-menu">
              <Dropdown.Item eventKey="">{filters.client ? "Limpiar" : "Seleccione"}</Dropdown.Item>
              {clientOptions.map((name) => (
                <Dropdown.Item key={name} eventKey={name}>
                  {name}
                </Dropdown.Item>
              ))}
            </Dropdown.Menu>
          </Dropdown>
          <Dropdown onSelect={(value) => handleFilterChange("building", value)}>
            <Dropdown.Toggle
              id="dropdown-building"
              className={`text-start ${filters.building !== "" ? "selected" : ""}`}
              data-selected={filters.building !== "" ? "true" : undefined}
            >
              {filters.building || "Edificios"}
            </Dropdown.Toggle>
            <Dropdown.Menu className="custom-dropdown-menu">
              <Dropdown.Item eventKey="">{filters.building ? "Limpiar" : "Seleccione"}</Dropdown.Item>
              {buildingOptions.map((name) => (
                <Dropdown.Item key={name} eventKey={name}>
                  {name}
                </Dropdown.Item>
              ))}
            </Dropdown.Menu>
          </Dropdown>
          <Dropdown onSelect={(value) => handleFilterChange("country", value)}>
            <Dropdown.Toggle
              id="dropdown-country"
              className={`text-start ${filters.country !== "" ? "selected" : ""}`}
              data-selected={filters.country !== "" ? "true" : undefined}
            >
              {filters.country || "Países"}
            </Dropdown.Toggle>
            <Dropdown.Menu className="custom-dropdown-menu">
              <Dropdown.Item eventKey="">{filters.country ? "Limpiar" : "Seleccione"}</Dropdown.Item>
              {countryOptions.map((name) => (
                <Dropdown.Item key={name} eventKey={name}>
                  {name}
                </Dropdown.Item>
              ))}
            </Dropdown.Menu>
          </Dropdown>
          <Dropdown onSelect={(value) => handleFilterChange("screenType", value)}>
            <Dropdown.Toggle
              id="dropdown-screenType"
              className={`text-start ${filters.screenType !== "" ? "selected" : ""}`}
              data-selected={filters.screenType !== "" ? "true" : undefined}
            >
              {filters.screenType || "Circuito"}
            </Dropdown.Toggle>
            <Dropdown.Menu className="custom-dropdown-menu">
              <Dropdown.Item eventKey="">{filters.screenType ? "Limpiar" : "Seleccione"}</Dropdown.Item>
              {screenTypeOptions.map((type) => (
                <Dropdown.Item key={type} eventKey={type}>
                  {type}
                </Dropdown.Item>
              ))}
            </Dropdown.Menu>
          </Dropdown>
        </div>
        <div className="photo-info">
          {Object.entries(getCurrentPhotoData()).map(([label, value]) => (
            <p key={label}>{value}</p>
          ))}
        </div>
        <div className="logo-container-galeria">
          <img src={logo} alt={`${pais} Logo`} className="logo" />
        </div>
      </div>
      <div className="right-section">
        <div className="carousel-wrapper">
          <Slider ref={sliderRef} {...sliderSettings} afterChange={(index) => setCurrentSlide(index)}>
            {filteredPhotos.length > 0 ? (
              filteredPhotos.map((photo, index) => (
                <div key={index} className="slide-item">
                  <img
                    src={photo.src}
                    alt={
                      photo.idcliente && clientes[photo.idcliente]
                        ? `Foto de ${clientes[photo.idcliente].nombre} (${photo.screenType})`
                        : photo.alt ||
                          `Sin cliente asociado - ${edificios[photo.idedificio] || "Edificio desconocido"} - ${photo.screenType}`
                    }
                    className="carousel-image"
                    loading="lazy"
                    onError={(e) => console.error("Image load error for:", photo.src, e)}
                  />
                </div>
              ))
            ) : (
              <div className="slide-item">
                <p>No hay fotos que coincidan con los filtros</p>
              </div>
            )}
          </Slider>
          <div className="carousel-controls">
            <button onClick={goToPrev} className="control-btn">
              <FaBackwardStep />
            </button>
            <button onClick={togglePlayPause} className="control-btn">
              {isPlaying ? <FaPause /> : <FaPlay />}
            </button>
            <button onClick={downloadImage} className="control-btn">
              <IoIosCloudDownload />
            </button>
            {isAdmin && (
              <button onClick={() => setShowUploadModal(true)} className="control-btn" title="Subir foto">
                <FaUpload />
              </button>
            )}
            {isAdmin && filteredPhotos[currentSlide] && (
              <button
                onClick={handleDeletePhoto}
                className="control-btn"
                title="Eliminar foto"
                style={{ color: "#dc3545" }}
              >
                <FaTrash />
              </button>
            )}
            <button onClick={goToNext} className="control-btn">
              <FaForwardStep />
            </button>
          </div>
        </div>
      </div>
      {isAdmin && (
        <SubirFotoModal
          show={showUploadModal}
          onHide={() => setShowUploadModal(false)}
          onUploadSuccess={() => {
            setShowUploadModal(false);
            refreshPhotos();
          }}
          edificiosList={Object.entries(edificios)
            .map(([id, data]) => ({ id: Number(id), edificio: data.edificio, pais: data.pais }))
            .filter((e) => (userId === 904 ? e.pais === "Chile" : e.pais !== "Uruguay"))
            .sort((a, b) => a.edificio.localeCompare(b.edificio))}
          clientesList={Object.entries(clientes)
            .map(([id, data]) => ({
              id: Number(id),
              nombre: data.nombre,
              categoria: data.categoria,
              pais: data.pais,
            }))
            .filter((c) => (userId === 904 ? c.pais === "Chile" : c.pais !== "Uruguay"))
            .sort((a, b) => (a.nombre || "").localeCompare(b.nombre || ""))}
          categorias={categorias}
          userId={userId}
        />
      )}
    </div>
  );
};

export default Galeria;
